import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Avatar,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';

const UsersAndGroupsTab = ({
  principals,
  selectedPrincipals,
  onSelectionChange,
  onChangePermissionSets,
  onRemoveAccess,
  onAssignUsersGroups,
  onPrincipalClick,
  onRefresh,
  isRefreshing,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter principals based on search
  const filteredPrincipals = principals.filter((principal) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return principal.principalName.toLowerCase().includes(query);
  });

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      onSelectionChange(filteredPrincipals.map((p) => p.assignmentIds).flat());
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectPrincipal = (assignmentIds) => {
    const allSelected = assignmentIds.every((id) => selectedPrincipals.includes(id));
    if (allSelected) {
      onSelectionChange(selectedPrincipals.filter((id) => !assignmentIds.includes(id)));
    } else {
      onSelectionChange([...new Set([...selectedPrincipals, ...assignmentIds])]);
    }
  };

  const isPrincipalSelected = (assignmentIds) => {
    return assignmentIds.every((id) => selectedPrincipals.includes(id));
  };

  const hasSelections = selectedPrincipals.length > 0;

  // Empty state
  if (principals.length === 0) {
    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#253E66',
            }}
          >
            Assigned users and groups (0)
          </Typography>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={onAssignUsersGroups}
            sx={{
              borderColor: '#4698D3',
              color: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#3580B8',
                backgroundColor: '#EBF5FB',
              },
            }}
          >
            Assign users or groups
          </Button>
        </Box>

        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontSize: '0.875rem',
            color: '#666666',
            mb: 3,
          }}
        >
          The following users and groups in IAM Identity Center can select this AWS account from within their AWS access portal.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            backgroundColor: '#F9FBFD',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              backgroundColor: '#EBF5FB',
              mb: 2,
            }}
          >
            <PersonIcon sx={{ fontSize: 28, color: '#4698D3' }} />
          </Avatar>
          <Typography
            sx={{
              color: '#253E66',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              mb: 1,
            }}
          >
            No users or groups assigned
          </Typography>
          <Typography
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.875rem',
              textAlign: 'center',
              maxWidth: 350,
            }}
          >
            Assign users or groups to give them access to this AWS account
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            fontSize: '1rem',
            color: '#253E66',
          }}
        >
          Assigned users and groups ({principals.length})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={onChangePermissionSets}
            disabled={!hasSelections}
            sx={{
              borderColor: hasSelections ? '#E5E5E5' : '#F0F0F0',
              color: hasSelections ? '#253E66' : '#BDBDBD',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#4698D3',
                backgroundColor: '#F9FBFD',
              },
            }}
          >
            Change permission sets
          </Button>
          <Button
            variant="outlined"
            onClick={onRemoveAccess}
            disabled={!hasSelections}
            sx={{
              borderColor: hasSelections ? '#E5E5E5' : '#F0F0F0',
              color: hasSelections ? '#253E66' : '#BDBDBD',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#E74C3C',
                color: '#E74C3C',
                backgroundColor: '#FDF2F2',
              },
            }}
          >
            Remove access
          </Button>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={onAssignUsersGroups}
            sx={{
              borderColor: '#4698D3',
              color: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#3580B8',
                backgroundColor: '#EBF5FB',
              },
            }}
          >
            Assign users or groups
          </Button>
          <IconButton
            onClick={onRefresh}
            disabled={isRefreshing}
            sx={{
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#EBF5FB',
                borderColor: '#4698D3',
              },
            }}
          >
            <RefreshIcon
              sx={{
                color: '#4698D3',
                fontSize: 20,
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        sx={{
          fontFamily: 'Metropolis, sans-serif',
          fontSize: '0.875rem',
          color: '#666666',
          mb: 2,
        }}
      >
        The following users and groups in IAM Identity Center can select this AWS account from within their AWS access portal.
      </Typography>

      {/* Search */}
      <TextField
        placeholder="Search users by username, search groups by group name"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        fullWidth
        sx={{
          mb: 3,
          maxWidth: 500,
          '& .MuiOutlinedInput-root': {
            fontFamily: 'Metropolis, sans-serif',
            backgroundColor: '#FFFFFF',
            borderRadius: '6px',
            '& fieldset': { borderColor: '#E5E5E5' },
            '&:hover fieldset': { borderColor: '#4698D3' },
            '&.Mui-focused fieldset': { borderColor: '#4698D3' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Table */}
      <TableContainer
        sx={{
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
              <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #E5E5E5', width: 50 }}>
                <Checkbox
                  checked={
                    filteredPrincipals.length > 0 &&
                    filteredPrincipals.every((p) => isPrincipalSelected(p.assignmentIds))
                  }
                  indeterminate={
                    selectedPrincipals.length > 0 &&
                    !filteredPrincipals.every((p) => isPrincipalSelected(p.assignmentIds))
                  }
                  onChange={handleSelectAll}
                  sx={{
                    color: '#E5E5E5',
                    '&.Mui-checked': { color: '#4698D3' },
                    '&.MuiCheckbox-indeterminate': { color: '#4698D3' },
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#253E66',
                  borderBottom: '1px solid #E5E5E5',
                }}
              >
                Username / group name
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#253E66',
                  borderBottom: '1px solid #E5E5E5',
                }}
              >
                Permission sets
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#253E66',
                  borderBottom: '1px solid #E5E5E5',
                  width: 120,
                }}
              >
                Type
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPrincipals.map((principal, index) => {
              const isSelected = isPrincipalSelected(principal.assignmentIds);
              const isUser = principal.principalType === 'USER';

              return (
                <TableRow
                  key={`${principal.principalType}:${principal.principalName}`}
                  hover
                  sx={{
                    backgroundColor: isSelected ? '#EBF5FB' : 'transparent',
                    '&:hover': {
                      backgroundColor: isSelected ? '#EBF5FB' : '#F9FBFD',
                    },
                    '&:last-child td': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #E5E5E5' }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectPrincipal(principal.assignmentIds)}
                      sx={{
                        color: '#E5E5E5',
                        '&.Mui-checked': { color: '#4698D3' },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                    <Typography
                      onClick={() => onPrincipalClick(principal.principalType, principal.principalName)}
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        color: '#4698D3',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textDecorationColor: 'transparent',
                        '&:hover': {
                          textDecorationColor: '#4698D3',
                        },
                      }}
                    >
                      {principal.principalName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {principal.permissionSets.map((ps) => (
                        <Box key={ps.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              backgroundColor: ps.isJIT ? '#9B59B6' : '#4698D3',
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontSize: '0.875rem',
                              color: ps.isJIT ? '#9B59B6' : '#4698D3',
                              fontWeight: 500,
                            }}
                          >
                            {ps.name}
                          </Typography>
                          {ps.isJIT && (
                            <Tooltip
                              title={ps.jitExpiresAt ? `JIT access expires: ${new Date(ps.jitExpiresAt).toLocaleString()}` : 'JIT temporary access'}
                              arrow
                            >
                              <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                                label="JIT"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  fontFamily: 'Metropolis, sans-serif',
                                  fontWeight: 600,
                                  backgroundColor: '#F5EBF8',
                                  color: '#9B59B6',
                                  border: '1px solid #9B59B6',
                                  '& .MuiChip-icon': {
                                    color: '#9B59B6',
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isUser ? (
                        <PersonIcon sx={{ fontSize: 18, color: '#666666' }} />
                      ) : (
                        <GroupIcon sx={{ fontSize: 18, color: '#666666' }} />
                      )}
                      <Typography
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '0.875rem',
                          color: '#666666',
                        }}
                      >
                        {isUser ? 'User' : 'Group'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* No search results */}
      {filteredPrincipals.length === 0 && searchQuery && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Typography
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.95rem',
            }}
          >
            No results match "{searchQuery}"
          </Typography>
        </Box>
      )}
    </Box>
    </motion.div>
  );
};

export default UsersAndGroupsTab;
